import { ReferralDetail, OriginalSource, statusLabel, type Detail } from './ReferralDetail';
export { ReferralDetail, reasonLabel } from './ReferralDetail';
import { useEffect, useState } from 'react';
import { useSearch } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';

import { clientApi } from '@/config/axios';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/lib/auth/AuthContext';
import { CoordinationQueue } from './CoordinationQueue';
import { ReferralSelection } from './ReferralSelection';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';

type Item = { title: string; id: string; status: string; reason: string | null; created_at: string };

export function AgentInbox() {
  const { organization } = useAuth();
  const org = organization?.organization_id;
  const base = `/api/v1/organizations/${org}/agent`;
  const { job: requestedJob } = useSearch({ strict: false }) as { job?: string };
  const [selected, setSelected] = useState<string | null>(requestedJob ?? null);
  useEffect(() => {
    setSelected(requestedJob ?? null);
  }, [org, requestedJob]);
  const [offset, setOffset] = useState(0);
  const jobs = useQuery({
    enabled: !!org,
    refetchInterval: 15000,
    queryKey: ['agent-jobs', org, offset],
    queryFn: async () =>
      (
        await clientApi.get<{ items: Item[]; has_more: boolean; enabled: boolean; remaining_runs: number }>(
          `${base}/proposals?offset=${offset}`,
        )
      ).data,
  });
  const detail = useQuery({
    refetchInterval: 15000,
    enabled: !!org && !!selected,
    queryKey: ['agent-detail', org, selected],
    queryFn: async () => (await clientApi.get<Detail>(`${base}/proposals/${selected}`)).data,
  });
  return (
    <main className="flex flex-col gap-6 p-6">
      <h1>Referral Agent Inbox</h1>
      <CoordinationQueue />
      <Alert>
        <AlertTitle>Development review</AlertTitle>
        <AlertDescription>
          Only the designated reviewer can access these proposals. Processing uses saved document text. Patient intake
          and checklist changes require your explicit review and confirmation.
        </AlertDescription>
      </Alert>
      {jobs.isError && (
        <Alert variant="destructive">
          <AlertTitle>Inbox unavailable</AlertTitle>
          <AlertDescription>
            Your practice may not have a designated reviewer or the required permissions.{' '}
            <Button variant="outline" onClick={() => jobs.refetch()}>
              Retry
            </Button>
          </AlertDescription>
        </Alert>
      )}
      {jobs.isPending && <p role="status">Loading referral inbox…</p>}
      {jobs.data && (
        <>
          {!jobs.data.enabled && <p>Agent processing is currently disabled.</p>}
          {!jobs.data.items.length && (
            <p>No referral proposals yet. Select existing faxes or uploads for review when processing is enabled.</p>
          )}
          <p>{jobs.data.remaining_runs} pilot runs remaining.</p>
          <ReferralSelection key={org} enabled={jobs.data.enabled && jobs.data.remaining_runs > 0} />
          <div className="grid items-start gap-6 lg:grid-cols-[16rem_1fr] xl:h-[calc(100dvh-7rem)]">
            <Card className="min-h-0 xl:max-h-full xl:overflow-y-auto">
              <CardHeader>
                <CardTitle>Referral worklist</CardTitle>
                <CardDescription>Source processing and review status</CardDescription>
              </CardHeader>
              <CardContent className="flex flex-col gap-3">
                {jobs.data.items.map((item) => (
                  <Button
                    key={item.id}
                    variant={selected === item.id ? 'secondary' : 'outline'}
                    className="h-auto justify-start whitespace-normal text-left"
                    onClick={() => setSelected(item.id)}>
                    {item.title} · {statusLabel(item.status)} · {new Date(item.created_at).toLocaleString()}
                  </Button>
                ))}
              </CardContent>
              <CardFooter className="gap-2">
                <Button variant="outline" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - 30))}>
                  Previous
                </Button>
                <Button variant="outline" disabled={!jobs.data.has_more} onClick={() => setOffset(offset + 30)}>
                  Next
                </Button>
              </CardFooter>
            </Card>
            {detail.isError ? (
              <Alert variant="destructive">
                <AlertTitle>Proposal unavailable</AlertTitle>
                <AlertDescription>
                  The source may have changed or access may have been restricted. Review the current document.
                </AlertDescription>
              </Alert>
            ) : detail.data ? (
              <div className="grid min-h-0 min-w-0 gap-6 xl:h-full xl:grid-cols-2">
                <ScrollArea type="always" className="min-h-0 xl:h-full" aria-label="Referral review details">
                  <ReferralDetail
                    key={`${org}:${detail.data.id}`}
                    detail={detail.data}
                    processingEnabled={jobs.data.enabled && jobs.data.remaining_runs > 0}
                  />
                </ScrollArea>
                <OriginalSource key={`${org}:${detail.data.id}:source`} base={base} jobId={detail.data.id} />
              </div>
            ) : (
              <p>Select a referral to inspect its evidence.</p>
            )}
          </div>
        </>
      )}
    </main>
  );
}
